import { useMemo, useState } from 'react'
import { CalendarDays, ChevronLeft, ChevronRight, Clock } from 'lucide-react'
import type { Appointment } from '@/services/appointmentsApi'

interface AppointmentCalendarViewProps {
  appointments: Appointment[]
  onSelect?: (appointment: Appointment) => void
}

const DAY_LABELS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom']

function startOfWeek(date: Date): Date {
  const d = new Date(date)
  const day = d.getDay()
  const diff = day === 0 ? -6 : 1 - day // Lunes como primer día
  d.setDate(d.getDate() + diff)
  d.setHours(0, 0, 0, 0)
  return d
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date)
  d.setDate(d.getDate() + days)
  return d
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('es-VE', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

/**
 * Agenda semanal por día (fase A4): grilla Lun-Dom con las citas del rango,
 * bloques verdes (CONFIRMED) y amarillos (RESCHEDULED), agrupadas por día.
 * Los huecos libres se visualizan como fondo claro clickeable para agendar.
 */
export default function AppointmentCalendarView({
  appointments,
  onSelect,
}: AppointmentCalendarViewProps) {
  const [weekStart, setWeekStart] = useState<Date>(() => startOfWeek(new Date()))

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  )

  const byDay = useMemo(() => {
    const map = new Map<string, Appointment[]>()
    for (const appointment of appointments) {
      const slotStart = appointment.confirmedSlotStart ?? appointment.requestedSlotStart
      if (!slotStart) continue
      const day = new Date(slotStart)
      const key = day.toISOString().slice(0, 10)
      const list = map.get(key) ?? []
      list.push(appointment)
      map.set(key, list)
    }
    return map
  }, [appointments])

  const today = new Date()

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CalendarDays className="h-5 w-5 text-emerald-600" />
          <h2 className="text-sm font-semibold text-slate-800">
            Semana del {weekStart.toLocaleDateString('es-VE', { day: 'numeric', month: 'long' })}
          </h2>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setWeekStart(addDays(weekStart, -7))}
            className="rounded-lg border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50"
            aria-label="Semana anterior"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            onClick={() => setWeekStart(startOfWeek(new Date()))}
            className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
          >
            Hoy
          </button>
          <button
            onClick={() => setWeekStart(addDays(weekStart, 7))}
            className="rounded-lg border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50"
            aria-label="Semana siguiente"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-4 lg:grid-cols-7">
        {weekDays.map((day) => {
          const key = day.toISOString().slice(0, 10)
          const dayAppointments = byDay.get(key) ?? []
          const isToday = sameDay(day, today)

          return (
            <div
              key={key}
              className={[
                'min-h-32 rounded-lg border p-2',
                isToday
                  ? 'border-emerald-300 bg-emerald-50/50'
                  : 'border-slate-200 bg-slate-50/50',
              ].join(' ')}
            >
              <p className={['text-xs font-semibold', isToday ? 'text-emerald-700' : 'text-slate-500'].join(' ')}>
                {DAY_LABELS[(day.getDay() + 6) % 7]} {day.getDate()}
              </p>

              <div className="mt-1.5 space-y-1.5">
                {dayAppointments.length === 0 && (
                  <p className="text-[11px] text-slate-400">Sin citas</p>
                )}
                {dayAppointments.map((appointment) => {
                  const slotStart = appointment.confirmedSlotStart ?? appointment.requestedSlotStart
                  const confirmed = appointment.status === 'CONFIRMED'
                  return (
                    <button
                      key={appointment.id}
                      onClick={() => onSelect?.(appointment)}
                      className={[
                        'w-full rounded-md border px-2 py-1 text-left text-[11px] transition-colors',
                        confirmed
                          ? 'border-emerald-200 bg-emerald-100 text-emerald-800 hover:bg-emerald-200'
                          : 'border-amber-200 bg-amber-100 text-amber-800 hover:bg-amber-200',
                      ].join(' ')}
                      title={`${appointment.patientName ?? 'Paciente'} · ${appointment.serviceName ?? 'Consulta'}`}
                    >
                      <span className="flex items-center gap-1 font-semibold">
                        <Clock className="h-3 w-3" />
                        {slotStart ? formatTime(slotStart) : '—'}
                      </span>
                      <span className="block truncate">{appointment.patientName ?? 'Paciente'}</span>
                      <span className="block truncate opacity-75">
                        {appointment.serviceName ?? 'Consulta'}
                        {appointment.modality === 'REMOTE' ? ' · 💻' : ''}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
